<template>
	<div
		v-if="gallery_options && gallery_options.length"
		class="gallery-modal-wrapper"
	>
		<div class="gallery-modal">
			<div class="gallery-modal__header">
				<p class="gallery-modal__count">
					{{ getCount() }}
				</p>
				<div class="gallery-modal__nav-buttons">
					<button
						class="gallery-modal__nav-prev"
						:class="{ 'gallery-modal__nav-prev--disabled': first_selected }"
						:disabled="first_selected"
						@click="viewPrev"
					/>
					<button
						class="gallery-modal__nav-next"
						:class="{ 'gallery-modal__nav-next--disabled': last_selected }"
						:disabled="last_selected"
						@click="viewNext"
					/>
				</div>
				<p
					v-if="selected_option.title"
					class="gallery-modal__title"
				>
					{{ selected_option.title }}
				</p>

				<div class="gallery-modal__buttons">
					<a
						v-if="config.downloadable && selected_option.image_url"
						class="gallery-modal__button"
						:href="selected_option_image_download_url"
					>
						Download
					</a>
				</div>

				<button
					class="gallery-modal__close-modal"
					@click="emit( 'close-modal' )"
				/>
			</div>

			<div
				class="gallery-modal__content"
				:class="{ 'gallery-modal__content--show-sidebar': show_comments }"
			>
				<div
					class="gallery-modal__content-wrapper"
					:class="{ 'gallery-modal__content-wrapper--full-resolution': show_full_resolution }"
				>
					<PageLoaderComponent
						v-if="loading"
					/>

					<img
						v-if="selected_option.image_url"
						v-show="!loading"
						class="gallery-modal__image"
						:class="{ 'gallery-modal__image--full-resolution': show_full_resolution }"
						:style="full_resolution_styles"
						:src="selected_option.image_url"
						@load="loading = false"
						@click="toggleFullResolution"
					>
					<video
						v-else-if="selected_option.video_url"
						v-show="!loading"
						class="gallery-modal__video"
						:src="selected_option.video_url"
						:alt="selected_option.title"
						autoplay
						controls
						@loadeddata="loading = false"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import PageLoaderComponent from '../PageLoaderComponent.vue';

import { onMounted, computed, ref, reactive, onUnmounted, watch, defineProps, defineEmits } from 'vue';

const props = defineProps( {
	options: {
		required: true,
		type: Array
	},
	config: {
		required: false,
		type: Object,
		default: () => {
			return {
				downloadable: false
			};
		}
	}
} );

const emit = defineEmits( [ 'close-modal', 'view-prev', 'view-next', 'fetch-more-results' ] );

const loading = ref( false );
const gallery_options = ref( [] );

onMounted( () => {
	window.addEventListener( 'keydown', handleKeyPress );
	gallery_options.value = [ ...props.options ];
} );

onUnmounted( () => {
	window.removeEventListener( 'keydown', handleKeyPress );
} );

const selected_option = computed( () => {
	return gallery_options.value.find( option => option.selected );
} );

const selected_option_image_download_url = computed( () => {
	const escaped_url = encodeURIComponent(
		selected_option.value.image_url
	);

	return `/download-option?url=${escaped_url}`;
} );

const first_selected = computed( () => {
	return gallery_options.value[0] && gallery_options.value[0].selected;
} );

const last_selected = computed( () => {
	return gallery_options.value.at( -1 ) && gallery_options.value.at( -1 ).selected;
} );

watch( last_selected, ( new_value, old_value ) => {
	if ( old_value === false && new_value === true ) {
		emit( 'fetch-more-results' );
	}
} );

function handleKeyPress( e ) {
	switch ( e.key ) {
		case 'ArrowRight':
			if ( last_selected.value ) {
				return;
			}
			return viewNext();
		case 'ArrowLeft':
			if ( first_selected.value ) {
				return;
			}
			return viewPrev();
		case 'Escape':
			return emit( 'close-modal' );
		default:
			return;
	}
}

function viewPrev() {
	loading.value = true;
	show_full_resolution.value = false;
	const current_selection_index = gallery_options.value.findIndex( option => option.selected );
	gallery_options.value[ current_selection_index ].selected = false;

	if ( current_selection_index === 0 ) {
		gallery_options.value[ gallery_options.value.length - 1 ].selected = true;
		loading.value = false;
		return;
	}

	gallery_options.value[ current_selection_index - 1 ].selected = true;
	loading.value = false;
}

function viewNext() {
	loading.value = true;
	show_full_resolution.value = false;
	const current_selection_index = gallery_options.value.findIndex( option => option.selected );
	gallery_options.value[ current_selection_index ].selected = false;

	if ( current_selection_index === gallery_options.value.length - 1 ) {
		gallery_options.value[0].selected = true;

		loading.value = false;
		return;
	}

	gallery_options.value[ current_selection_index + 1 ].selected = true;
	loading.value = false;
}

watch(
	() => props.options,
	( new_options, old_options ) => {
		if ( new_options === old_options ) {
			return;
		}

		const currently_selected_id = selected_option.value.id;
		gallery_options.value = options.map( option => {
			return {
				...option,
				selected: currently_selected_id === option.id
			};
		} );
	}
);

// Click to zoom
const show_full_resolution = ref( false );
const full_resolution_coordinates = reactive( {
	x: null,
	y: null
} );

const full_resolution_styles = computed( () => {
	if ( !show_full_resolution.value ) {
		return null;
	}
	return {
		objectPosition: `${full_resolution_coordinates.x} ${full_resolution_coordinates.y}`
	};
} );

function toggleFullResolution( e ) {
	show_full_resolution.value = !show_full_resolution.value;
	if ( !show_full_resolution.value ) {
		full_resolution_coordinates.x = null;
		full_resolution_coordinates.y = null;
	}

	full_resolution_coordinates.x = `${e.offsetX / e.target.offsetWidth * 100}%`;
	full_resolution_coordinates.y = `${e.offsetY / e.target.offsetHeight * 100}%`;
}

function getCount() {
	const selected_states = gallery_options.value.map( option => option.selected );
	const current_selection = selected_states.indexOf( true ) + 1;

	return `${ current_selection }/${ gallery_options.value.length }`;
}
</script>